











































































































































































































































































































































import { PropType } from 'vue';
import { mapActions, mapGetters } from 'vuex';
import { get } from 'vuex-pathify';

import mixins from '@/utils/mixins';
import Validatable from '@/mixins/validatable';
import BindsAttrs from '@/mixins/binds-attrs';

import {
  Quote,
  IQuoteServiceItemData,
  IQuoteServiceTemplate,
  QuoteServiceItem,
  QuoteServiceTemplate,
  IQuoteServiceItem
} from '@/models';
import { ErrorHandler } from '@/models/errors';
import { omit } from 'lodash';

import QuoteServiceComputationSelector from '../quote-service-computation/QuoteServiceComputationSelector.vue';
import { ErrorManager } from '@/models/error';
import QuillPDFEditor from '../common/QuillPDFEditor.vue';
import QuoteTemplateNoteDropDownItem from './section/QuoteTemplateNoteDropDownItem.vue';
import QuoteTemplateNoteTooltipIcon from './templates/common/QuoteTemplateNoteTooltipIcon.vue';

const baseMixins = mixins(BindsAttrs, Validatable);

interface Options extends InstanceType<typeof baseMixins> {
  /* eslint-disable-next-line camelcase */
  $_modelEvent: string;
}

export default baseMixins
  .extend<Options>({
    functional: false
  })
  .extend({
    components: {
      QuoteServiceComputationSelector,
      QuillPDFEditor,
      QuoteTemplateNoteDropDownItem,
      QuoteTemplateNoteTooltipIcon
    },
    name: 'QuoteServiceItemsSection',
    props: {
      editable: Boolean,
      exchangeRate: Number,
      currencyCode: String,

      language: {
        type: String
      },

      errors: {
        type: Object as PropType<ErrorHandler>
      },

      quote: { type: Quote, required: false }
    },

    data: () => ({
      selectedService: null as null | number,
      loading: false,
      errorMessage: ''
    }),

    watch: {
      internalValue: {
        handler() {
          this.validate();
        },
        deep: true
      }
    },

    mounted() {
      this.fetchServices();
    },

    computed: {
      ...mapGetters({
        getQuoteServiceTemplateById: 'quoteTemplate/getQuoteServiceTemplateById'
      }),
      quoteServiceTemplates: get<IQuoteServiceTemplate[]>(
        'quoteTemplate/getQuoteServiceTemplates'
      ),

      requiredRule() {
        return (field: string) => [
          (v: string) => !!v || `${field} is required`
        ];
      }
    },

    beforeCreate() {
      // v-radio-group needs to emit a different event
      // https://github.com/vuetifyjs/vuetify/issues/4752
      this.$_modelEvent =
        (this.$options.model && this.$options.model.event) || 'input';
    },

    methods: {
      ...mapActions({
        fetchQuoteServiceTemplates: 'quoteTemplate/fetchQuoteServiceTemplates'
      }),

      clearAllErrors() {
        this.errorMessage = '';
      },

      fetchServices() {
        this.clearAllErrors();
        this.loading = true;
        return this.fetchQuoteServiceTemplates()
          .catch(error => {
            this.errorMessage = ErrorManager.extractApiError(error);
          })
          .finally(() => {
            this.loading = false;
          });
      },

      removeServiceItem(item: IQuoteServiceTemplate, index?: number) {
        this.clearAllErrors();

        const itemIndex = index ?? this.internalValue.indexOf(item);
        this.internalValue.splice(itemIndex, 1);
      },

      addServiceItem() {
        this.clearAllErrors();

        this.internalValue.push(new QuoteServiceTemplate());
      },

      setService(value: IQuoteServiceTemplate, index: number) {
        this.clearAllErrors();

        const serviceItemData: IQuoteServiceItemData = {
          template: value['id']!,
          ...omit(value, ['id']),
          price: 0,
          price_computation: null
        };

        //https://vuejs.org/v2/guide/reactivity.html#For-Arrays.
        this.$set(
          this.internalValue,
          index,
          new QuoteServiceItem(serviceItemData)
        );

        this.selectedService = null;
      },

      clearSelectedValue(index: number) {
        this.internalValue[index].id = null;
        this.internalValue[index].template = null;
        this.internalValue[index].title = '';
        this.internalValue[index].description = '';
        this.internalValue[index].price_computation = null;
        this.internalValue[index].modified = false;
      },

      priceManuallyChanged(index: number) {
        // clear template price_computation whenever price is manually set
        this.internalValue[index].price_computation = null;
      },

      setPriceAndDays(
        {
          computation,
          price,
          days
        }: { computation: number; price: number; days: number },
        index: number
      ) {
        this.internalValue[index].price_computation = computation;
        this.internalValue[index].price = price;
        this.internalValue[index].days = days;
      },

      setTemplateModified(index: number) {
        // set template modfied flag to true whenever text changes
        // to prevent a translation lookup for custom text
        this.internalValue[index].modified = true;
      },

      serviceItemTemplateNote(serviceItem: IQuoteServiceItem) {
        const template = this.getQuoteServiceTemplateById(serviceItem.template);
        let note = '';
        if (template) {
          note = template.note;
        }
        return note;
      }
    }
  });
